import React, { useState } from 'react';
import { publicUrl, thumbUrl } from '../lib/fileUrl';

// A row of photo/video/document thumbnails, tap any to open it full size.
//
// Lived inside AdminDashboard until the homeowner's own work order needed the
// same thing: their photos, per line, on the request they submitted. Same
// component both sides, so a customer sees exactly the thumbnails Julian does.
//
// Cloudinary serves video from a /video/ path and keeps the original
// extension, so either tells us not to try rendering it as an <img>.
export const isVideo = (url) => /\.(mp4|mov|webm|m4v)(\?|$)/i.test(url) || url.includes('/video/');

// A PDF, on the other hand, comes back down the /image/ path — Cloudinary
// treats it as an image it could rasterise. So the path says nothing and only
// the extension does. Without this check a customer's inspection report
// renders as a broken <img> on Julian's lead card.
export const isPdf = (url) => /\.pdf(\?|$)/i.test(url || '');

// Matches DocumentList's icon, because they are the same idea in two places:
// a file you open rather than a picture you look at.
export const IconDoc = ({ size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M14 2.5H7A1.5 1.5 0 0 0 5.5 4v16A1.5 1.5 0 0 0 7 21.5h10a1.5 1.5 0 0 0 1.5-1.5V7z" />
    <path d="M14 2.5V7h4.5" />
  </svg>
);

// The tile a PDF gets instead of a preview. Deliberately not a rendered first
// page: an inspection report is megabytes and this strip is drawn on a phone,
// on cell data, every time a lead card opens. It says PDF and opens on tap.
export const DocThumb = ({ onClick, title }) => (
  <div className="photo-thumb photo-thumb-doc" onClick={onClick} title={title}>
    <IconDoc />
    <span>PDF</span>
  </div>
);

export default function MediaStrip({ urls, label }) {
  // Which ones the browser could not load. A broken-image icon tells a person
  // nothing; it looks like the app lost their photograph. A tile that says the
  // picture cannot be opened, which still opens the link, says what happened.
  const [broken, setBroken] = useState({});
  if (!urls?.length) return null;

  const open = (u) => (e) => { e.stopPropagation(); window.open(u, '_blank', 'noopener'); };

  return (
    <div style={{ margin: '8px 0 2px' }}>
      {label && <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 4 }}>{label} ({urls.length})</div>}
      <div className="photo-strip">
        {urls.map((raw, i) => {
          // Repaired on the way in. Records written before the server stored
          // proper URLs hold a filesystem path, and that is what turned a
          // thumbnail into a broken image and a tap into a trip to the home
          // screen. See lib/fileUrl.js.
          const u = publicUrl(raw);
          if (!u) return null;
          if (broken[i]) {
            // The RAW stored value in the tooltip, not the repaired one. When a
            // photograph will not load the only useful question is what the
            // record actually holds — a Cloudinary URL that is being refused,
            // or a filesystem path to a file that is no longer there. Those
            // need opposite fixes and look identical from the outside.
            return (
              <button key={i} type="button" className="photo-thumb photo-thumb-gone"
                onClick={open(u)} title={`Stored as: ${raw}`}>
                Can&rsquo;t open
              </button>
            );
          }
          if (isPdf(u)) return <DocThumb key={i} onClick={open(u)} title={u.split('/').pop()} />;
          if (isVideo(u)) return <div key={i} className="photo-thumb photo-thumb-video" onClick={open(u)}>▶</div>;
          // thumbUrl for the picture, publicUrl for the link. Cloudinary is
          // asked to hand back a format this browser can decode, which is the
          // difference between a photograph and a broken frame when the
          // technician shot it on an iPhone. Opening it full size gives the
          // original, untouched.
          return (
            <img key={i} src={thumbUrl(raw)} alt="" className="photo-thumb" loading="lazy"
              onClick={open(u)}
              onError={() => setBroken((b) => ({ ...b, [i]: true }))} />
          );
        })}
      </div>
    </div>
  );
}
